// oxlint-disable no-console

import { Nullable } from '@/utils/customtypes.util';
import {
  PaymentInputModel,
  PaymentResponseModel,
  RefundInputModel,
  RefundResponseModel,
} from '../models/payment.model';
import { PaymentProvider } from '../payment-provider';
import axios from 'axios';
import { FiservPaymentResponseModel } from '../models/fiserv/payment-response.model';
import DateUtil from '@/utils/date.util';
import { getPrinterService } from '@/utils/printer/printer';
import store from '@/store';
import i18n from '@/locales';

interface PinpadFiservConfig {
  endpoint: string;
}

export class FiservUtil implements PaymentProvider {
  config = ((): Nullable<PinpadFiservConfig> => {
    try {
      return store.getters.pinpadConfigFiserv;
    } catch {
      return null;
    }
  })();

  get paymentEndpoint(): string {
    return `${this.config?.endpoint}/api/v1/fiserv/payments`;
  }

  get refundEndpoint(): string {
    return `${this.config?.endpoint}/api/v1/fiserv/refunds`;
  }

  get initOnStart(): boolean {
    return false;
  }

  get requiresInteractiveCancel(): boolean {
    return true;
  }

  async init(): Promise<object> {
    if (!this.config) {
      return Promise.reject(i18n.global.t('errors.pinpadConfiguration'));
    }

    return {};
  }

  executePayment(data: PaymentInputModel): Promise<PaymentResponseModel> {
    return axios
      .post<FiservPaymentResponseModel>(this.paymentEndpoint, {
        amount: data.amount,
        tip: data.tip,
      })
      .then((response) => {
        console.log(response);

        const responseData = {
          transactionId: response.data.authorization,
          reference: response.data.reference,
          authorization: response.data.authorization,
          total: response.data.amount,
          amount: data.amount,
          tip: data.tip,
          cardType: response.data.cardBrand,
          date: new Date().toISOString(),
          ticket: FiservUtil.getChargeVoucher(response.data),
          merchant: null,
        };

        const printerService = getPrinterService();
        if (responseData.ticket) {
          printerService
            .getConfig()
            .then((config) => {
              printerService.print(config, responseData.ticket ?? '');
            })
            .catch((err) => {
              console.log(err);
              // ignore
            });
        }

        return responseData;
      })
      .catch((error) => {
        const errorDetail = error.response?.data;
        const message =
          errorDetail?.detail ?? errorDetail?.title ?? error.message;

        const payment =
          errorDetail?.paymentData as Nullable<FiservPaymentResponseModel>;
        if (payment?.memberId) {
          const printerService = getPrinterService();
          printerService
            .getConfig()
            .then((config) => {
              const ticket = FiservUtil.getDeclinedVoucher(message, payment);
              printerService.print(config, ticket);
            })
            .catch((err) => {
              console.log(err);
              // ignore
            });
        }

        return Promise.reject(message);
      });
  }

  cancel(data: RefundInputModel): Promise<RefundResponseModel> {
    return axios
      .post<FiservPaymentResponseModel>(this.refundEndpoint, {
        amount: data.amount,
        tip: data.tip,
      })
      .then((response) => {
        console.log(response);

        return {
          transactionId: response.data.authorization,
          reference: response.data.reference,
          authorization: response.data.authorization,
          amount: response.data.amount,
          cardType: response.data.cardBrand,
          date: new Date().toISOString(),
          ticket: FiservUtil.getRefundVoucher(response.data),
        };
      })
      .catch((error) => {
        console.log(error);
        return Promise.reject(error.detail ?? error.title ?? error.message);
      });
  }

  public static getChargeVoucher(data: FiservPaymentResponseModel) {
    const date = DateUtil.formatUtcString(data.date, 'YYYY-MM-DD');
    const time = DateUtil.formatUtcString(data.date, 'HH:mm:ss');
    const cardData = `${data.cardIsCredit ? 'CREDITO' : 'DEBITO'}/ ${
      data.cardBank
    } / ${data.cardBrand}`;

    const ticket = `
      VENTA
      ${data.merchantName}

      Afiliacion: ${data.memberId}

      ${date}        ${time}
      ********${data.cardLast4}            AUT: ${data.authorization}
      ${cardData}

      IMPORTE   $        ${data.amount.toFixed(2)}
      PROPINA   $        ${data.tip.toFixed(2)}
      TOTAL     $        ${data.total.toFixed(2)}

      APROBADA EN LINEA
      ARQC: ${data.arqc}    TSI:   ${data.tsi}
      AID: ${data.aid}

      COPIA CLIENTE
      ${data.cardHolder}

      VISITA
      FIRSTDATA.COM
    `;

    return ticket;
  }

  public static getRefundVoucher(data: FiservPaymentResponseModel) {
    const date = DateUtil.formatUtcString(data.date, 'YYYY-MM-DD');
    const time = DateUtil.formatUtcString(data.date, 'HH:mm:ss');
    const cardData = `${data.cardIsCredit ? 'CREDITO' : 'DEBITO'}/ ${
      data.cardBank
    } / ${data.cardBrand}`;

    const ticket = `
      DEVOLUCION
      ${data.merchantName}

      Afiliacion: ${data.memberId}

      ${date}        ${time}
      ********${data.cardLast4}            AUT: ${data.authorization}
      ${cardData}

      IMPORTE   $        ${data.amount.toFixed(2)}
      PROPINA   $        ${data.tip.toFixed(2)}
      TOTAL     $        ${data.total.toFixed(2)}

      APROBADA EN LINEA
      ARQC: ${data.arqc}    TSI:   ${data.tsi}
      AID: ${data.aid}

      COPIA CLIENTE
      ${data.cardHolder}

      VISITA
      FIRSTDATA.COM
    `;

    return ticket;
  }

  public static getDeclinedVoucher(
    message: string,
    data: FiservPaymentResponseModel
  ) {
    const cardData = `${data.cardIsCredit ? 'CREDITO' : 'DEBITO'}/ ${
      data.cardBank
    } / ${data.cardBrand}`;

    const ticket = `
      VENTA
      ${data.merchantName}

      Afiliacion: ${data.memberId}

      MID: ${data.merchantId}        TID: ${data.terminalId}
      ********${data.cardLast4}            AUT: ${data.authorization}
      ${cardData}

      ${message}
      No se ha realizado ningun cobro a tu tarjeta.
    `;

    return ticket;
  }
}
